"use client";

import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { usePathname } from "next/navigation";

// Navigation sections for the homepage
const homeSections = [
  { name: "Hero", href: "#herosection", icon: "home" },
  { name: "Compare", href: "#beforeaftersection", icon: "compare" },
  { name: "Reviews", href: "#testimonials", icon: "reviews" },
  { name: "Get Started", href: "#howitworkssection", icon: "start" },
  { name: "The Game", href: "#gamefeaturesection", icon: "game" },
  { name: "Team", href: "#aboutus", icon: "team" },
  { name: "FAQ", href: "#faqsection", icon: "faq" },
];

// Navigation sections for the families page
const familiesSections = [
  { name: "Hero", href: "#families-hero", icon: "home" },
  { name: "The Game", href: "#families-game", icon: "game" },
  { name: "Why Cove", href: "#families-why", icon: "start" },
  { name: "Features", href: "#families-features", icon: "compare" },
  { name: "Safety", href: "#families-trust", icon: "trust" },
  { name: "Pricing", href: "#families-pricing", icon: "pricing" },
  { name: "FAQ", href: "#families-faq", icon: "faq" },
];

// Simple icons as SVG components
function ArrowUpIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 19V5M5 12l7-7 7 7" />
    </svg>
  );
}

function MenuIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="1" />
      <circle cx="12" cy="5" r="1" />
      <circle cx="12" cy="19" r="1" />
    </svg>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  );
}

// Section icon component
function SectionIcon({ type, className }: { type: string; className?: string }) {
  const icons: Record<string, React.ReactNode> = {
    home: (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
      </svg>
    ),
    compare: (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="18" rx="1" />
        <rect x="14" y="3" width="7" height="18" rx="1" />
      </svg>
    ),
    reviews: (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
      </svg>
    ),
    start: (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <polygon points="10 8 16 12 10 16 10 8" fill="currentColor" stroke="none" />
      </svg>
    ),
    game: (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="6" width="20" height="12" rx="2" />
        <path d="M6 12h4M8 10v4" />
        <circle cx="17" cy="10" r="1" fill="currentColor" />
        <circle cx="15" cy="13" r="1" fill="currentColor" />
      </svg>
    ),
    team: (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
      </svg>
    ),
    faq: (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3" />
        <circle cx="12" cy="17" r="0.5" fill="currentColor" />
      </svg>
    ),
    trust: (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <path d="M9 12l2 2 4-4" />
      </svg>
    ),
    pricing: (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 6v12M9 9h6a2 2 0 010 4h-6M9 13h5a2 2 0 010 4H9" />
      </svg>
    ),
  };
  return <>{icons[type] || null}</>;
}

export default function FloatingNav() {
  const [isVisible, setIsVisible] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const [isOverDark, setIsOverDark] = useState(false);
  const pathname = usePathname();

  // Determine which page we're on
  const isHomePage = pathname === "/";
  const isFamiliesPage = pathname === "/families";
  const isSupported = isHomePage || isFamiliesPage;

  // Get the appropriate sections for the current page
  const sections = isFamiliesPage ? familiesSections : homeSections;

  // Dark section selectors for each page
  const darkSectionSelector = isFamiliesPage
    ? "#families-game, #families-worries, #families-pricing, #families-cta, footer"
    : "#ctasection, #gamefeaturesection, footer";

  // Track scroll position to show/hide the widget and detect dark sections
  useEffect(() => {
    if (!isSupported) return;

    const handleScroll = () => {
      // Show after scrolling down 400px
      setIsVisible(window.scrollY > 400);

      // Widget position (bottom-right corner, 24px from edges, button is 48px tall)
      const widgetBottom = window.innerHeight - 24; // bottom-6
      const widgetTop = widgetBottom - 48; // button height

      // Check if widget overlaps any dark section by comparing bounding rects
      let overDark = false;

      // Get all dark sections for the current page
      const darkElements = document.querySelectorAll(darkSectionSelector);

      for (const el of darkElements) {
        const rect = el.getBoundingClientRect();

        // Check if the widget's vertical position overlaps with this section
        // Widget is at the bottom of viewport, so we check if the section
        // extends into where the widget is
        if (rect.top < widgetBottom && rect.bottom > widgetTop) {
          overDark = true;
          break;
        }
      }

      setIsOverDark(overDark);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll(); // Check initial position

    return () => window.removeEventListener("scroll", handleScroll);
  }, [isSupported, darkSectionSelector]);

  // Track which section is currently in view
  useEffect(() => {
    if (!isSupported) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(`#${entry.target.id}`);
          }
        });
      },
      { threshold: 0.3, rootMargin: "-20% 0px -60% 0px" }
    );

    sections.forEach(({ href }) => {
      const element = document.querySelector(href);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [isSupported, sections]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setIsMenuOpen(false);
  };

  const scrollToSection = (href: string) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    setIsMenuOpen(false);
  };

  if (!isSupported) return null;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.9 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-2"
        >
          {/* Section Menu */}
          <AnimatePresence>
            {isMenuOpen && (
              <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                transition={{ duration: 0.15 }}
                className={cn(
                  "rounded-2xl p-2 shadow-2xl backdrop-blur-sm transition-colors duration-300",
                  isOverDark
                    ? "bg-[#FFFFF0] border border-black/5"
                    : "bg-[#232526] border border-white/10"
                )}
              >
                <div className="flex flex-col gap-1">
                  {sections.map((section) => (
                    <button
                      key={section.href}
                      onClick={() => scrollToSection(section.href)}
                      className={cn(
                        "flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium",
                        "transition-all duration-150",
                        isOverDark
                          ? cn(
                              "hover:bg-black/5",
                              activeSection === section.href
                                ? "text-[#232526] bg-black/5"
                                : "text-black/50 hover:text-[#232526]"
                            )
                          : cn(
                              "hover:bg-white/10",
                              activeSection === section.href
                                ? "text-white bg-white/10"
                                : "text-white/60 hover:text-white"
                            )
                      )}
                    >
                      <SectionIcon type={section.icon} className="w-4 h-4" />
                      <span>{section.name}</span>
                      {activeSection === section.href && (
                        <span className="ml-auto w-1.5 h-1.5 rounded-full bg-[#6ACE57]" />
                      )}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Control Buttons */}
          <div className="flex items-center gap-2">
            {/* Menu Toggle Button */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={cn(
                "w-12 h-12 rounded-full flex items-center justify-center shadow-lg",
                "transition-all duration-300 ease-out",
                // Base colors - switch based on background
                isOverDark
                  ? "bg-[#FFFFF0] text-[#232526] border border-black/5"
                  : "bg-[#232526] text-white border border-white/10",
                // Active/hover states
                isMenuOpen
                  ? "!bg-gradient-to-r from-[#6ACE57] to-[#07C8E6] !text-black !border-transparent"
                  : "hover:bg-gradient-to-r hover:from-[#6ACE57] hover:to-[#07C8E6] hover:text-black hover:border-transparent"
              )}
              aria-label={isMenuOpen ? "Close navigation menu" : "Open navigation menu"}
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? (
                <CloseIcon className="w-5 h-5" />
              ) : (
                <MenuIcon className="w-5 h-5" />
              )}
            </motion.button>

            {/* Back to Top Button */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={scrollToTop}
              className={cn(
                "w-12 h-12 rounded-full flex items-center justify-center shadow-lg",
                "transition-all duration-300 ease-out",
                // Base colors - switch based on background
                isOverDark
                  ? "bg-[#FFFFF0] text-[#232526] border border-black/5"
                  : "bg-[#232526] text-white border border-white/10",
                // Hover state
                "hover:bg-gradient-to-r hover:from-[#6ACE57] hover:to-[#07C8E6] hover:text-black hover:border-transparent"
              )}
              aria-label="Scroll to top"
            >
              <ArrowUpIcon className="w-5 h-5" />
            </motion.button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
